import { ChatMessageDetails } from "@virtualprogaming/js-public-sdk";
import { isEmpty, map } from "lodash-es";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";

import { useChatConversationMessages } from "~/queries";
import { LoadingIcon } from "~/ui/components/icons";
import { NoContentFound } from "~/ui/components/noContentFound";
import { useChatLeftMenuSettings } from "~/ui/context";
import { useScreenMd } from "~/utils";

import { Message } from "./Message";

const MESSAGES_LIMIT = 30;

export const Messages: FC<{
  chatId: number;
  accessToken: string;
  isSending: boolean;
  hasClickedLoadMore: boolean;
  setHasClickedLoadMore: (loadMore: boolean) => void;
}> = ({
  chatId,
  accessToken,
  isSending,
  hasClickedLoadMore,
  setHasClickedLoadMore,
}) => {
  const [messagesLimit, setMessagesLimit] = useState(MESSAGES_LIMIT);

  const { data, isLoading } = useChatConversationMessages(
    accessToken,
    chatId,
    messagesLimit
  );
  const messagesStartRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { isMenuOpened } = useChatLeftMenuSettings();
  const isDesktop = useScreenMd();

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    setHasClickedLoadMore(false);
  }, [setHasClickedLoadMore]);
  const scrollToTop = () => {
    messagesStartRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (!hasClickedLoadMore) {
      scrollToBottom();
    } else {
      scrollToTop();
    }
  }, [data?.count, hasClickedLoadMore, scrollToBottom]);

  const onLoadMore = () => {
    setHasClickedLoadMore(true);
    setMessagesLimit(
      (prevMessagesLimit: number) => prevMessagesLimit + MESSAGES_LIMIT
    );
  };
  return (
    <>
      {isLoading && (
        <div className="flex justify-center w-100 items-center ">
          <LoadingIcon className="absolute top-300px" />
        </div>
      )}
      {data?.count && messagesLimit < data?.count ? (
        isMenuOpened && !isDesktop ? null : (
          <div className="flex justify-center w-100 items-center ">
            <span
              className="bg-transparent-55 pointer pd-lg pd-t-sm pd-b-sm borderR-1 absolute top-150px z-index-1010"
              onClick={onLoadMore}
            >
              <FormattedMessage defaultMessage="Load more" id="00LcfG" />
            </span>
          </div>
        )
      ) : null}
      <div className="h-95 bg-dark pd-md scroll-auto scroll-hide w-95 mg-0-auto overflow-x-hidden border-bottom">
        <div ref={messagesStartRef} />

        {isEmpty(data?.data) && !isLoading ? (
          <NoContentFound>
            <FormattedMessage
              defaultMessage="No messages are found"
              id="ywZuzE"
            />
          </NoContentFound>
        ) : (
          data?.data &&
          map(
            [...(data?.data ?? [])].reverse(),
            (message: ChatMessageDetails) => (
              <Message
                key={message.id}
                message={message}
                isSending={isSending}
              />
            )
          )
        )}
        <div ref={messagesEndRef} />
      </div>
    </>
  );
};
